import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, useRef } from 'react';
import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { mergeProps } from '@react-aria/utils';
import { useSelectState } from '@react-stately/select';
import classNames from 'classnames';
import { PureButton } from '../../basics';
import { ChevronIcon, HelperText } from '../../components';
import { ButtonType, DropdownType, InputSizes, ValidationStates } from '../../enums';
import { commonStyles, dropdownStyles } from '../scss';
import { DropdownSelectMenu } from './DropdownSelectMenu';
import { getActiveItem, getKeyItemRecord, mapToAriaProps } from './helper';
import { Overlay } from './Overlay';
import styles from './Dropdown.module.scss';
const Dropdown = forwardRef(({ ariaLabel, className, dataTestId, defaultOpen, disabled, dropdownType = DropdownType.Primary, helperText, helperTextClasses, isFloating, label, leftComponent, name, onChange, onClick, onClose, onFocusChange, options, overlayClasses, rightComponent, showCheckIcon, showHelperText, size, validationState, value, ...restProps }, forwardedRef) => {
    const buttonRef = useRef(null);
    const activeItem = getActiveItem(options, value);
    const labelId = useId();
    const helperId = useId();
    const propsWithChildren = mapToAriaProps(options, ariaLabel, labelId, label);
    const state = useSelectState({
        ...propsWithChildren,
        onSelectionChange: key => {
            const keyItemRecord = getKeyItemRecord(options);
            if (keyItemRecord[key]?.value &&
                keyItemRecord[key]?.value !== value) {
                onChange(keyItemRecord[key].value);
            }
        },
        isDisabled: disabled,
        isOpen: defaultOpen,
        // TODO: Needs a closer investigation to resolve the type interference properly
        // @ts-expect-error Key types differ
        selectedKey: value,
    });
    const { menuProps, triggerProps, valueProps } = useSelect(propsWithChildren, state, buttonRef);
    const { buttonProps } = useButton({
        ...mergeProps(triggerProps, {
            onPress: () => onClick?.(),
            onFocusChange: (isFocused) => onFocusChange?.(isFocused),
        }),
        isDisabled: disabled,
    }, buttonRef);
    const { isOpen } = state;
    const { focusProps } = useFocusRing();
    const isError = validationState === ValidationStates.Error;
    const isSmall = size === InputSizes.Small;
    const isSecondary = dropdownType === DropdownType.Secondary;
    const handleOnClose = () => {
        if (onClose) {
            onClose();
        }
        state.close();
    };
    return (_jsxs("div", { className: classNames('position-relative', {
            [commonStyles.isDisabled]: disabled,
        }, className), ref: forwardedRef, children: [_jsx(HiddenSelect, { isDisabled: disabled, state: state, triggerRef: buttonRef, label: label, name: name }), _jsxs(PureButton, { ...mergeProps(buttonProps, focusProps), ...restProps, type: ButtonType.Button, ref: buttonRef, disabled: disabled, className: classNames('d-flex align-items-center justify-content-between w-100 overflow-visible m-0', dropdownStyles.dropdownPureButton, styles.dropdownButton, {
                    [styles.isOpen]: isOpen,
                    [styles.isSmall]: isSmall,
                    [styles.isSecondary]: dropdownType === DropdownType.Secondary,
                    [styles.isError]: isError,
                }), "data-testid": dataTestId, "aria-invalid": isError, "aria-describedby": showHelperText && !isOpen ? helperId : undefined, children: [leftComponent && (_jsx("span", { className: "d-flex justify-content-center align-items-center mr-0-5x", children: leftComponent })), _jsx("div", { className: "screenReaderOnly", children: ariaLabel }), _jsxs("div", { ...valueProps, className: "text-truncate w-100 text-start d-flex align-items-center", "aria-hidden": true, children: [activeItem?.label || label, rightComponent && (_jsx("div", { className: "d-flex align-items-center ml-1x", children: rightComponent }))] }), label && isFloating && !!value && (_jsx("span", { id: labelId, className: classNames('text-truncate w-100 text-start', styles.floatedLabel, {
                            [styles.isError]: isError,
                            [styles.isSecondary]: isSecondary,
                        }), children: label })), label && isFloating && (_jsx("legend", { id: labelId, className: classNames(commonStyles.hiddenLegend, {
                            'd-block': !!value,
                            [styles.isSmall]: isSmall,
                        }), children: label })), _jsx(ChevronIcon, { className: classNames(dropdownStyles.chevronIcon, {
                            [dropdownStyles.isOpen]: isOpen,
                        }), size: 24, ariaHidden: true })] }), showHelperText && !isOpen && (_jsx(HelperText, { id: helperId, className: helperTextClasses, validationState: validationState, children: helperText })), isOpen && (_jsx(Overlay, { dropdownType: dropdownType, isOpen: isOpen, overlayClasses: overlayClasses, onClose: handleOnClose, children: _jsx(DropdownSelectMenu, { showCheckIcon: showCheckIcon, ariaProps: menuProps, state: state, options: options, ariaLabel: ariaLabel, ariaLabelledBy: labelId }) }))] }));
});
Dropdown.displayName = Dropdown.name;
export { Dropdown };
